import { getPlatformDisplayName } from 'src/utils/platform-label';

import { listingsWord } from './listing-count-words';

/**
 * What the Delist & Relist results screen tells the seller.
 *
 * Delist & relist runs in two places at once. A background job replaces the posts Voolist can reach
 * over an API (eBay, Etsy, Shopify…), and the seller's own browser replaces the Depop and Poshmark
 * ones, because only the extension can touch those. The results screen is fed by the JOB, so for a
 * listing that lives only on Depop the job finishes having correctly done nothing — and that read
 * as a green "Success" next to "0/0", while the browser was either still working or had refused the
 * listing outright for a missing field and deliberately left the post alone.
 *
 * So the rule is: this screen may only report what the job did. Work that belongs to the browser is
 * named as the browser's, and its real outcome arrives per listing as its own message.
 */

/**
 * A listing the job did nothing for, because every marketplace it was asked about is the browser's.
 *
 * Asked as "did the job touch anything?" rather than "is every platform a browser one?" — the job
 * reports what it delisted and what it failed to delist, so both being empty IS "it touched
 * nothing", and a listing that is half API and half browser still reports its API half honestly.
 *
 * @param {Object} detail - one listing's result row from the job
 * @returns {boolean}
 */
export function isBrowserOnly(detail) {
  return (detail?.browserPlatforms || []).length > 0
    && (detail?.delistedFrom || []).length === 0
    && (detail?.failedDelist || []).length === 0;
}

/** The marketplaces across this run that the browser is replacing, in words a seller reads. */
export function browserPlatformNames(details) {
  return [...new Set((details || []).flatMap(d => d?.browserPlatforms || []))]
    .map(getPlatformDisplayName);
}

/**
 * The status shown against one listing.
 *
 * @param {Object} detail - one listing's result row
 * @returns {{label: string, color: string}}
 */
export function rowStatus(detail) {
  if (isBrowserOnly(detail)) return { label: 'In your browser', color: 'info' };
  if (detail?.status === 'success') return { label: 'Done', color: 'success' };
  if (detail?.status === 'partial') return { label: 'Partly done', color: 'warning' };
  if (detail?.status === 'failed' || detail?.status === 'error') return { label: 'Failed', color: 'error' };
  return { label: 'Skipped', color: 'default' };
}

/**
 * Why the job left listings alone: those with no stock left, which are never re-listed, and those
 * whose selected marketplaces matched nothing on them. The job names the reason on each row.
 *
 * @param {number} skipped - the job's skipped count
 * @param {Array<Object>} details - the job's per-listing rows
 * @returns {{outOfStock: number, unmatched: number}}
 */
function skippedBreakdown(skipped, details) {
  const outOfStock = details.filter(d => d?.status === 'skipped' && d?.skipReason === 'outOfStock').length;
  return { outOfStock, unmatched: Math.max(skipped - outOfStock, 0) };
}

/**
 * The one-line summary at the top of the results screen.
 *
 * @param {Object} results - the job's batch result
 * @returns {{severity: string, message: string}}
 */
export function delistRelistSummary(results) {
  const {
    total, success, partial, failed, skipped = 0, hasSelection = false, details = [],
  } = results || {};

  const { outOfStock, unmatched } = skippedBreakdown(skipped, details);
  const skippedSuffix = [
    unmatched > 0
      ? ` ${listingsWord(unmatched)} skipped: not on the marketplaces you chose.`
      : '',
    outOfStock > 0
      ? ` ${listingsWord(outOfStock)} skipped because ${outOfStock !== 1 ? "they're" : "it's"} out of stock.`
      : '',
  ].join('');
  // "the marketplaces you chose" vs "all their marketplaces": match the wording to what actually ran.
  const scope = hasSelection ? 'on the marketplaces you chose' : 'on all their marketplaces';

  const names = browserPlatformNames(details);
  const browserOnly = details.filter(isBrowserOnly).length;
  const processed = details.filter(d => d?.status !== 'skipped').length;

  // Everything asked for is the browser's work, so the job finished without touching a marketplace.
  // Saying it succeeded would answer a question the seller did not ask.
  if (browserOnly > 0 && browserOnly === processed) {
    return {
      severity: 'info',
      message: `This browser is relisting ${listingsWord(browserOnly)} on ${names.join(' and ')}. You'll get a message for each one as it finishes.${skippedSuffix}`,
    };
  }

  // The job did some of the work, so it reports that half and names the rest as the browser's.
  const browserNote = names.length > 0
    ? ` ${names.join(' and ')} listings are relisted from this browser. You'll get a message for each one.`
    : '';

  if (success === total) {
    return {
      severity: 'success',
      message: `${listingsWord(success)} delisted and relisted ${scope}.${browserNote}`,
    };
  }
  if (success === 0 && partial === 0 && failed === 0 && skipped > 0) {
    const reasons = [
      unmatched > 0 ? `${listingsWord(unmatched)} ${unmatched !== 1 ? "aren't" : "isn't"} on the marketplaces you chose` : null,
      outOfStock > 0
        ? `${listingsWord(outOfStock)} ${outOfStock !== 1 ? 'are' : 'is'} out of stock, and out-of-stock listings aren't relisted`
        : null,
    ].filter(Boolean);
    return {
      severity: 'warning',
      message: `Nothing was delisted or relisted: ${reasons.join('; ')}.${browserNote}`,
    };
  }
  if (failed === total) {
    return {
      severity: 'error',
      message: `Couldn't delist and relist ${listingsWord(failed)}. Click the arrow on a row to see why.${skippedSuffix}${browserNote}`,
    };
  }
  // Only the outcomes this run actually had, so a seller never reads "0 partly relisted".
  const tally = [[success, 'relisted'], [partial, 'partly relisted'], [failed, 'failed']]
    .filter(([count]) => count > 0)
    .map(([count, outcome]) => `${count} ${outcome}`);
  return {
    severity: 'warning',
    message: `${tally.length ? `${tally.join(', ')}.` : ''}${skippedSuffix}${browserNote}`.trim(),
  };
}

export default delistRelistSummary;
